import uniqBy from 'lodash/uniqBy';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CHAT_LIST_PAGE_SIZE,
  chatListApiParams,
  groupChatsByDate
} from 'src/content/Chat/utils';
import useLazyQuery from 'src/hooks/useLazyQuery';
import { HISTORY_EXCLUDED_CLASSES } from 'src/layouts/SidebarV2/constants';
import type { ChatHistoryGroup } from 'src/layouts/SidebarV2/types';
import { setChats, updateChatsPageNumber } from 'src/redux/slices/chat';
import type { RootState } from 'src/redux/store';
import { useDispatch, useSelector } from 'src/redux/store';
import { useLazyGetChatsQuery } from 'src/services/api';
import type { DataApiInputParams } from 'src/types/api';
import type { ChatData } from 'src/types/chat';
import type { CHAT_CLASS } from 'src/types/enum';
import { ErrorContext } from 'src/utils/errorMappings';
import { TRANSLATION_CONSTANTS as T } from 'src/utils/translations';

interface GetChatsResponse {
  data?: ChatData[];
}

export interface UseChatHistory {
  groups: ChatHistoryGroup[];
  chatCount: number;
  hasMore: boolean;
  isFetching: boolean;
  loadMoreRows: () => void;
}

export const useChatHistory = (): UseChatHistory => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const chats = useSelector(
    (state: RootState) => state.chat.chats
  ) as ChatData[];
  const chatsPageNumber = useSelector(
    (state: RootState) => state.chat.chatsPageNumber
  );
  const [page, setPage] = useState<number>(chatsPageNumber);

  const [fetchChats, { data, isFetching }] = useLazyQuery<
    DataApiInputParams,
    GetChatsResponse
  >({ api: useLazyGetChatsQuery, errorContext: ErrorContext.CHAT });

  useEffect(() => {
    dispatch(updateChatsPageNumber(page));
    /* `chats` is a shared Redux slice that outlives this hook's own mount —
     * a consumer that mounts/unmounts repeatedly (e.g. the collapsed-rail
     * hover flyout) would otherwise re-fetch and re-append a page it already
     * has every time it remounts, duplicating those rows. Skip the fetch
     * when this page's worth of data is already present. */
    if (chats.length >= CHAT_LIST_PAGE_SIZE * (page + 1)) {
      return;
    }
    fetchChats({ params: chatListApiParams(page) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, dispatch, fetchChats]);

  useEffect(() => {
    if (data?.data) {
      /* `chats` is shared app-wide state, also written to directly by
       * `appendChat` (e.g. when a new chat/message arrives elsewhere in the
       * app) independent of this pagination merge — the same chat id can
       * land in `chats` via both paths, so dedupe on merge. V1's equivalent
       * history panel already does this (HistoryPanel.tsx); this hook
       * previously dropped it, which reintroduced the same duplicate-row bug. */
      dispatch(setChats(uniqBy([...chats, ...data.data], 'id')));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const visibleChats = useMemo(
    () =>
      chats.filter(
        (chat: ChatData) =>
          !HISTORY_EXCLUDED_CLASSES.includes(chat.chat_class as CHAT_CLASS)
      ),
    [chats]
  );

  const groups = useMemo<ChatHistoryGroup[]>(() => {
    const grouped = groupChatsByDate(visibleChats);
    const toItems = (list: ChatData[]) =>
      list.map((chat) => ({
        id: chat.id,
        title: chat.subject,
        chatClass: chat.chat_class,
        rid: chat.rid
      }));
    return [
      { id: 'today', heading: t(T.today), chats: toItems(grouped.today) },
      {
        id: 'thisWeek',
        heading: t(T.thisWeek),
        chats: toItems(grouped.thisWeek)
      },
      {
        id: 'thisMonth',
        heading: t(T.thisMonth),
        chats: toItems(grouped.thisMonthAndOlder)
      }
    ].filter((group) => group.chats.length > 0);
  }, [visibleChats, t]);

  const hasMore = chats.length >= CHAT_LIST_PAGE_SIZE * (page + 1);

  const loadMoreRows = useCallback(() => {
    if (!isFetching && hasMore) {
      setPage((prev) => prev + 1);
    }
  }, [isFetching, hasMore]);

  return {
    groups,
    chatCount: visibleChats.length,
    hasMore,
    isFetching,
    loadMoreRows
  };
};
